/* eslint-disable @typescript-eslint/no-explicit-any */
import { memoizedToDebugContent } from '@csp/csp-common-content';
import { ContentDeliveryService } from '@csp/csp-common-content-delivery-client';
import { CspError, Maybe } from '@csp/csp-common-model';
import { ScheduleVisitsContent } from '@csp/csp-common-visits';
import {
  DeviceSpecificationContentModel,
  DeviceSpecificationContentModelsMock,
  DevicesScheduleContentModel,
  LearnMoreContentModel,
  LearnMoreContentModelMock,
  LearnMoreIdentifierType,
  MedicationScheduleContentModel,
  PatientManagementContentModel,
  QuestionnaireInfoContentModel,
  QuestionnaireInfoContentModelMock,
  QuestionnaireInfoInfoType,
  RandomisationStatusContentModel,
  RandomisationStatusContentModelMock,
  RequestTagsContentModel,
  RpmMonitoringContentModel,
  RpmNoticeContentModel,
  StudyOverviewContentModel,
  StudyOverviewContentModelMock,
  StudyStatusContentModel,
  StudyStatusContentModelMock,
  StudyWebContentPackage,
  SystemLinkContentModel,
  TechnicalSupportContentModel,
  TechnicalSupportContentModelMock,
  VisitsScheduleContentModel,
  VisitsScheduleContentModelMock,
} from '@csp/csp-web-content-definitions';
import { DevicesScheduleContentModelsMock } from '@csp/csp-web-content-definitions/src/lib/contentModels/mocks/DevicesScheduleContentModels.mock';
import { RpmMonitoringContentModelMock } from '@csp/csp-web-content-definitions/src/lib/contentModels/mocks/RpmMonitoringContentModel.mock';
import { RpmNoticeContentModelMock } from '@csp/csp-web-content-definitions/src/lib/contentModels/mocks/RpmNoticeContentModel.mock';
import { StudyDefinedStatusTexts } from '../../feature/patient-study-defined-status/study-defined-status-card/model/StudyDefinedStatusTexts';
import { getOrDefault } from '../util/content/getOrDefault';
import { isDebugCmsModeActive } from '../util/debugCmsUtil';
import { GenericContentService } from './GenericContentService';

let studyPackage: StudyWebContentPackage | undefined = undefined;

const devicesScheduleMock = DevicesScheduleContentModelsMock.createMultiple();
const deviceSpecificationMock = DeviceSpecificationContentModelsMock.create();
const learnMoreMock = LearnMoreContentModelMock.create();
const questionnaireInfoMock = QuestionnaireInfoContentModelMock.create();
const rpmMonitoringMock = RpmMonitoringContentModelMock.create();
const rpmNoticeMock = RpmNoticeContentModelMock.create();
const studyOverviewMock = StudyOverviewContentModelMock.create();
const studyStatusMock = StudyStatusContentModelMock.create();
const technicalSupportMock = TechnicalSupportContentModelMock.create();
const visitScheduleMock = VisitsScheduleContentModelMock.create();
const randomizationStatusMock = RandomisationStatusContentModelMock.create();

const init = async (): Promise<void> => {
  const isDebugEnabled = isDebugCmsModeActive();
  const unifyStudyPackage = await ContentDeliveryService.fetchContentPackage('study');
  studyPackage = unifyStudyPackage as unknown as StudyWebContentPackage;
  if (isDebugEnabled) {
    studyPackage = memoizedToDebugContent<any>(studyPackage, '', '[CS]');
  }
};

const get = (): StudyWebContentPackage => {
  if (!studyPackage) {
    throw CspError.badState('Must call StudyContentService.init() before trying to access study content');
  }
  return studyPackage;
};

const getLearnMore = (identifier: LearnMoreIdentifierType): LearnMoreContentModel => {
  const learnMore = get().learn_more.find(learnMore => learnMore.identifier === identifier);

  return getOrDefault(
    learnMore,
    `Learn more content entry with identifier ${identifier} does not exist in the study package`,
    learnMoreMock,
  );
};

const getStudyStatus = (): StudyStatusContentModel => {
  const studyStatus = get().study_status?.[0];

  return getOrDefault(studyStatus, `Study status content entry does not exist in the study package`, studyStatusMock);
};

const getRandomizationStatus = (): RandomisationStatusContentModel => {
  const randomisationStatus = get().randomisation_status?.[0] ?? GenericContentService.getRandomisationStatus();
  return getOrDefault(
    randomisationStatus,
    `Randomisation status content entry does not exist in the study or generic package`,
    randomizationStatusMock,
  );
};

const getStudyDefinedStatus = (type: string): Maybe<StudyDefinedStatusTexts> => {
  const studyStatus = getStudyStatus().study_defined_statuses?.find(status => status.status_type === type);
  return studyStatus?.status?.[0];
};

const getVisitScheduleContentModel = (): VisitsScheduleContentModel[] => {
  const visitSchedule = get().visits_schedule;
  return getOrDefault(visitSchedule, `Visit texts does not exist in the study package`, visitScheduleMock, []);
};

const getScheduleVisitsContent = (): ScheduleVisitsContent => {
  const contentModel = getVisitScheduleContentModel();
  return ScheduleVisitsContent.fromCmsScheduleVisits(contentModel);
};

const getSystemLinks = (): SystemLinkContentModel[] => get().system_link;

const getMedicationSchedule = (): MedicationScheduleContentModel[] => get().medication_schedule;

const getStudyOverview = (): StudyOverviewContentModel => {
  const studyOverview = get().study_overview?.[0];
  return getOrDefault(
    studyOverview,
    `Study overview content entry does not exist in the study package`,
    studyOverviewMock,
  );
};

const getPatientManagement = (): Maybe<PatientManagementContentModel> => get().patient_management?.[0];

const getQuestionnaireInfos = (): QuestionnaireInfoContentModel => {
  const questionnaireInfos = get().questionnaire_info[0];

  return getOrDefault(
    questionnaireInfos,
    `Questionnaire info content entry does not exist in the study package`,
    questionnaireInfoMock,
  );
};

const getQuestionnaireInfo = (questionnaireCode: string): Maybe<QuestionnaireInfoInfoType['info']> => {
  const questionnaireInfo = getQuestionnaireInfos().info?.find(
    ({ info }) => info?.questionnaire_code === questionnaireCode,
  );
  return questionnaireInfo?.info;
};

const getRequestTags = (): Maybe<RequestTagsContentModel> => get().request_tags?.[0];

const getRpmMonitoring = (): RpmMonitoringContentModel => {
  const rpmMonitoring = get().rpm_monitoring?.[0];

  return getOrDefault(
    rpmMonitoring,
    `Rpm monitoring content entry does not exist in the study package`,
    rpmMonitoringMock,
  );
};

const getRpmNotices = (): RpmNoticeContentModel[] => {
  const rpmNotices = get().rpm_notice;

  return getOrDefault(rpmNotices, `Rpm notice content entry does not exist in the study package`, [rpmNoticeMock]);
};

const getTechnicalSupport = (): TechnicalSupportContentModel => {
  const technicalSupport = get().technical_support?.[0];

  return getOrDefault(
    technicalSupport,
    'Technical support content entry does not exist in the study package',
    technicalSupportMock,
  );
};

const getDevicesScheduleContent = (): DevicesScheduleContentModel[] => {
  const deviceSchedule = get().devices_schedule;

  return getOrDefault(
    deviceSchedule,
    `No devices schedule content entries exist in the study package`,
    devicesScheduleMock,
  );
};

const getDeviceSpecificationsContent = (): DeviceSpecificationContentModel[] => {
  const deviceSpecification = get().device_specification;

  return getOrDefault(
    deviceSpecification,
    `No device specification content entries exist in the study package`,
    deviceSpecificationMock,
  );
};

const getDeviceSpecificationContentInfo = (deviceCode: string): Maybe<DeviceSpecificationContentModel> => {
  const deviceSensorInfo = getDeviceSpecificationsContent().find(
    device => device.identifier?.identifier === deviceCode,
  );

  return deviceSensorInfo;
};

export const StudyContentService = {
  get,
  getDevicesScheduleContent,
  getDeviceSpecificationsContent,
  getDeviceSpecificationContentInfo,
  getLearnMore,
  getMedicationSchedule,
  getPatientManagement,
  getQuestionnaireInfo,
  getRequestTags,
  getRpmMonitoring,
  getRpmNotices,
  getScheduleVisitsContent,
  getStudyDefinedStatus,
  getStudyOverview,
  getStudyStatus,
  getSystemLinks,
  getTechnicalSupport,
  getVisitScheduleContentModel,
  getRandomizationStatus,
  init,
};
